// The floating day pill (Mac `MacDayPillModel` in ios/SourcesMac/MacConversationView.swift, iOS
// `dayPillVisible`, Android `FloatingDayPill`; PARITY P309). While the history moves, the day of the
// TOP row floats over it, and a beat (1.4 s) after the list stops it fades. Near the bottom it never
// shows: the newest messages are "today" and the inline separators already say so.
//
// Kept off the conversation (the Mac's 2026-10-17 lesson: the top row changes once per message
// scrolled past, and a conversation-level state re-rendered every row mid-scroll) — the page's
// `DayPill` component owns one of these and is the only thing that re-renders.

export const dayPillHideMillis = 1400;

export interface DayPillState {
  topPostId: string | null;
  visible: boolean;
}

type Schedule = (fn: () => void, ms: number) => unknown;
type Cancel = (handle: unknown) => void;

export class DayPillModel {
  private state: DayPillState = { topPostId: null, visible: false };
  private generation = 0;
  private timer: unknown = null;

  constructor(
    private readonly onChange: (s: DayPillState) => void,
    private readonly schedule: Schedule = (fn, ms) => setTimeout(fn, ms),
    private readonly cancel: Cancel = (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  ) {}

  get snapshot(): DayPillState {
    return this.state;
  }

  private set(next: DayPillState): void {
    if (next.topPostId === this.state.topPostId && next.visible === this.state.visible) return;
    this.state = next;
    this.onChange(next);
  }

  /** The list moved: `id` is its top row now. */
  topRowChanged(id: string, nearBottom: boolean): void {
    if (nearBottom) {
      // Back at the newest messages: nothing to float (the Mac's overlay hides on `nearBottom`).
      this.set({ topPostId: id, visible: false });
      return;
    }
    this.set({ topPostId: id, visible: true });
    this.generation += 1;
    const mine = this.generation;
    if (this.timer != null) this.cancel(this.timer);
    this.timer = this.schedule(() => {
      if (this.generation !== mine) return;
      this.timer = null;
      this.set({ ...this.state, visible: false });
    }, dayPillHideMillis);
  }

  dispose(): void {
    if (this.timer != null) this.cancel(this.timer);
    this.timer = null;
  }
}
